import React from 'react';
import {MdTextField} from 'core/components/fields/fields';
import {createForm} from 'core/form/createForm';
import {FormProps} from 'core/form/formUtils';
import StyledSelect from 'core/components/fields/styledSelect';
import StyledDatePicker from 'core/components/fields/styledDatePicker';
import {BlockComp} from 'core/components/blockComp/blockComp';
import ActiveItem from 'core/components/blockComp/activeItem';
import {Validate} from 'core/form/validate';
import {StatusActiveComp, StatusActiveComp__h5_style} from "core/components/buttons/statusActiveComp";
import {Product} from "product/productDomain";
import {observer} from "mobx-react";
import {DefinitionType, FIELDDEFDISPLAYTYPE} from "core/form/form";
import {ClosedFormBlock, ClosedFormBlockProps, ClosedFormField} from "core/form/closedForm";
import productService from "../productService";
import securityService from "core/security/securityService";
import {RESTRICTION} from "core/security/securityDomain";

interface ProductFormState {
  originalProductNumber : string;
}

@observer
class ProductForm extends React.Component<FormProps<Product, Product>, ProductFormState> {

  state = {originalProductNumber: this.props.dataObject.productNumber};

  validateProductNumber = (productNumber : string) : void => {
    const {dataObject} = this.props;
    const {originalProductNumber} = this.state;

    if (!productNumber || productNumber.length == 0) {
      return;
    }

    productNumber = productNumber.trim();

    if (dataObject.getId() && originalProductNumber && productNumber === originalProductNumber.trim()) {
      return;
    }

    productService.isProductNumberUnique(productNumber).then(
      isUniqueProductNumber  => {

        const form = dataObject.getForm(definition.formName);
        if (!isUniqueProductNumber) {
          form.getField("productNumber").valid = false;
          form.getField("productNumber").showValidation = true;
          form.getField("productNumber").props.errorText = "Product number " + productNumber + " already exists";
        }
      }
    );
  };

  componentDidUpdate(prevProps: Readonly<FormProps<Product, Product>>, prevState: Readonly<undefined>, prevContext: any): void {
    this.validateProductNumber(this.props.dataObject.productNumber);
  }

  componentDidMount(): void {
    this.validateProductNumber(this.props.dataObject.productNumber);
  }

  render() {

    let {dataObject, rootObject, entities, entity} = this.props;
    
    let fields = dataObject.getFormFields(definition.formName);
    let {productNumber, name, productType, specification, comment, introductionDate, salesDiscontinuationDate, supportDiscontinuationDate, unitOfMeasure, profitCenter, vatRate, taxType, revenueCategory} = fields;

    let profitCenterOptions = (securityService.currentUser.hasRestriction(RESTRICTION.LIMIT_PRODUCT_BY_USER_PROFITCENTER)) ?
      profitCenter.props.options.filter(lv => securityService.restrictionData.userProfitCenterIds.includes(lv.value)) : profitCenter.props.options;
    let openContent = (
      <div>

        {/*  2. row  */}
        <div className="row">
          <div className="fx col-xs-6 col-sm-2">
            <StyledSelect {...productType.props} />
            <Validate field={productType} value={productType.props.value} />
          </div>
          <div className="fx col-xs-6 col-sm-3">
            <MdTextField {...productNumber.props} />
            <Validate field={productNumber} value={productNumber.props.value.trim()} />
          </div>
          <div className="fx col-xs-12 col-sm-7">
            <MdTextField {...name.props} />
            <Validate field={name} value={name.props.value} />
          </div>
        </div>

        <div className="row">
          <div className="fx col-xs-12">
            <MdTextField {...specification.props} multiLine={true} rows={3} />
          </div>
        </div>

        <div className="row">
          <div className="fx col-xs-12">
            <MdTextField {...comment.props} multiLine={true} rows={3} />
          </div>
        </div>

        <div className="row">
          <div className="fx col-xs-6 col-sm-4">
            <StyledDatePicker {...introductionDate.props} />
          </div>
          <div className="fx col-xs-6 col-sm-4">
            <StyledDatePicker {...salesDiscontinuationDate.props} />
          </div>
          <div className="fx col-xs-6 col-sm-4">
            <StyledDatePicker {...supportDiscontinuationDate.props} />
          </div>
        </div>

        <div className="row">
          <div className="fx col-xs-6 col-sm-4">
            <StyledSelect {...unitOfMeasure.props} />
          </div>
          <div className="fx col-xs-6 col-sm-4">
            <StyledSelect {...profitCenter.props} options={profitCenterOptions} />
          </div>
          <div className="fx col-xs-6 col-sm-4">
            <StyledSelect {...vatRate.props} />
            <Validate field={vatRate} value={vatRate.props.value} />
          </div>
        </div>

        <div className="row">
        <div className="fx col-xs-6 col-sm-4">
            <StyledSelect {...taxType.props} />
          </div>
          <div className="fx col-xs-6 col-sm-4">
            <StyledSelect {...revenueCategory.props} />
          </div>
        </div>

      </div>
    );


    return (
      <BlockComp noPadding>
        <ActiveItem openContent={openContent}
                    closedContent={<ClosedContent {...{ dataObject, entity, entities, fields }} />}
                    validate={this.props.validate} {...this.props} />
      </BlockComp>
    );
  }
}


const ClosedContent = ({ dataObject, entity, entities, fields, disabled }: ClosedFormBlockProps<Product>) => (
  <div style={{ width: '100%' }}>

    <div style={{ display: 'flex' }}>
      {disabled ? null : <StatusActiveComp activeObject={dataObject} entity={entity} id={dataObject.productId} />}
      <h5 style={StatusActiveComp__h5_style}> {dataObject.productNumber} {dataObject.name} </h5>
    </div>


    <ClosedFormBlock {...{dataObject, entity, entities, fields}} >
      <ClosedFormField label={fields.specification.textLabel} value={dataObject.specification}/>
      <ClosedFormField label={fields.comment.textLabel} value={dataObject.comment}/>
      <ClosedFormField label={fields.introductionDate.textLabel} value={dataObject.introductionDate}/>
      <ClosedFormField label={fields.salesDiscontinuationDate.textLabel} value={dataObject.salesDiscontinuationDate}/>
      <ClosedFormField label={fields.supportDiscontinuationDate.textLabel} value={dataObject.supportDiscontinuationDate}/>
      <ClosedFormField label={fields.productType.textLabel} value={dataObject.productType}/>
      <ClosedFormField label={fields.unitOfMeasure.textLabel} value={dataObject.unitOfMeasure}/>
      <ClosedFormField label={fields.profitCenter.textLabel} value={dataObject.profitCenter}/>
      <ClosedFormField label={fields.revenueCategory.textLabel} value={dataObject.revenueCategory}/>
      <ClosedFormField label={fields.vatRate.textLabel} value={dataObject.vatRate}/>
      <ClosedFormField label={fields.taxType.textLabel} value={dataObject.taxType}/>
    </ClosedFormBlock>

  </div>
);


export const definition: DefinitionType = {
  formName: 'ProductForm',
  fields: [{
    name: 'productNumber',
    validators: ['IsRequired']
  }, {
    name: 'name',
    validators: ['IsRequired']
  }, {
    name: 'productType',
    validators: ['IsRequired']
  }, {
    name: 'vatRate',
    validators: ['IsRequired']
  }, {
    name: 'specification',
    displayType:  FIELDDEFDISPLAYTYPE.textArea
  }, {
    name: 'comment',
    displayType: FIELDDEFDISPLAYTYPE.textArea
  }, {
    name: 'introductionDate'
  }, {
    name: 'salesDiscontinuationDate'
  }, {
    name: 'supportDiscontinuationDate'
  }, {
    name: 'unitOfMeasure'
  }, {
    name: 'profitCenter'
  }, {
    name: 'revenueCategory'
  }, {
    name: 'taxType'
  }]
};

export default createForm(definition, ClosedContent)(ProductForm);






// WEBPACK FOOTER //
// ./src/product/component/productForm.tsx